/**
 * Parse Interactive Encoding
 *
 * Parses trailing encoding from assistant messages (suggested-response style).
 * Supports:
 * - Bracket form: [form]{json}[/form] (easy to parse; show loader until [/form] then render)
 * - Simple form: [/form:name,email,phone] or [/form:Title|name,email,phone]
 * - Legacy: JSON line then "[/form]" on next line
 * - Date: [/date] or [/date:Title]
 * - Time: [/time] or [/time:09:00-17:00]
 *
 * Primary responsibilities:
 * - Parse form (bracket + simple + legacy), date, time
 * - Return cleaned content and InteractiveSuggestion[] for the embed
 * - hasFormPending(): true when content has [form] but not [/form] (show loader)
 */

import type {
  InteractiveSuggestion,
  FormSuggestion,
  FormFieldSpec,
  ScheduleCalendarSuggestion,
  ScheduleTimeSuggestion,
  ScheduleSlot,
  OptionsSuggestion,
  OptionsOption
} from '@/types/nexus-interactive-suggestions'

/** Full form: [form]{json}[/form] - parse JSON between tags and strip block */
const BRACKET_FORM_REGEX = /\[form\]\s*([\s\S]*?)\s*\[\/form\]/
/** Options (single/multiple choice): [options]{json}[/options] */
const BRACKET_OPTIONS_REGEX = /\[options\]\s*([\s\S]*?)\s*\[\/options\]/
const INLINE_ENCODING_REGEX = /\[\/(form|date|time)(?::([^\]]*))?\]\s*$/

type FieldType = 'text' | 'email' | 'tel' | 'textarea' | 'number'

function fieldIdToType(id: string): FieldType {
  const lower = id.toLowerCase()
  if (lower.includes('email')) return 'email'
  if (lower.includes('phone') || lower.includes('tel')) return 'tel'
  return 'text'
}

function normalizeFieldType(t: string): FieldType {
  const v = (t || 'text').toLowerCase()
  if (v === 'email' || v === 'tel' || v === 'textarea' || v === 'number') return v as FieldType
  return 'text'
}

/** Parse bracket form: [form]{...json...}[/form]. */
function tryParseBracketForm(content: string): { cleanContent: string; suggestion: FormSuggestion } | null {
  const match = content.match(BRACKET_FORM_REGEX)
  if (!match) return null
  const jsonSource = match[1].trim()
  const parsed = safeParseFormJson(jsonSource)
  if (!parsed) return null
  const cleanContent = content.replace(match[0], '').trim()
  return { cleanContent, suggestion: parsed }
}

/** Parse legacy structured form: content ends with a JSON line then "\n[/form]". */
function tryParseStructuredForm(content: string): { cleanContent: string; suggestion: FormSuggestion } | null {
  const trimmed = content.trimEnd()
  const marker = '\n[/form]'
  const idx = trimmed.lastIndexOf(marker)
  if (idx === -1) return null
  const before = trimmed.slice(0, idx)
  const lineStart = before.lastIndexOf('\n') + 1
  const jsonSource = before.slice(lineStart).trim()
  const parsed = safeParseFormJson(jsonSource)
  if (parsed) return { cleanContent: before.slice(0, lineStart).trimEnd(), suggestion: parsed }
  return null
}

/** True when content has [form] but not yet [/form] - show loader until callout. */
export function hasFormPending(content: string | null | undefined): boolean {
  return typeof content === 'string' && content.includes('[form]') && !content.includes('[/form]')
}

/** Content to show while streaming when form is pending: hide raw [form]... and show up to that point. */
export function contentUntilFormPending(content: string | null | undefined): string {
  const sourceContent = typeof content === 'string' ? content : ''
  if (!hasFormPending(sourceContent)) return sourceContent
  const idx = sourceContent.indexOf('[form]')
  return idx === -1 ? sourceContent : sourceContent.slice(0, idx).trimEnd()
}

/** Strip any trailing widget encoding that starts with [/ so it never shows in the UI. */
export function stripTrailingWidgetEncoding(content: string | null | undefined): string {
  if (!content || typeof content !== 'string') return ''
  let s = content.trimEnd()
  let changed = true
  while (changed) {
    changed = false
    const match = s.match(/\s*\[\/(?:form|date|time)[^\]]*\]\s*$/)
    if (match) {
      s = s.slice(0, s.length - match[0].length).trimEnd()
      changed = true
    }
  }
  return s
}

/** Strip incomplete trailing encoding during streaming (e.g. "[/form:name" without closing "]"). */
export function stripIncompleteTrailingEncoding(content: string | null | undefined): string {
  if (!content || typeof content !== 'string') return ''
  const s = content.trimEnd()
  const incomplete = s.match(/\s*\[\/(?:form|date|time)[^\]]*$/)
  if (incomplete) return s.slice(0, s.length - incomplete[0].length).trimEnd()
  return s
}

/** Strip <function_calls>...</function_calls> markers from content. */
export function stripFunctionCallsMarkers(content: string | null | undefined): string {
  if (!content || typeof content !== 'string') return ''
  // Strip complete function_calls blocks
  let s = content.replace(/<function_calls>[\s\S]*?<\/function_calls>/gi, '')
  // Strip incomplete trailing function_calls (during streaming)
  s = s.replace(/<function_calls>[\s\S]*$/gi, '')
  // Also strip the closing tag if orphaned
  s = s.replace(/<\/function_calls>/gi, '')
  // Strip widget XML markers that LLMs sometimes emit as text
  // e.g. <show_contact_form fields="name,email,phone" />
  s = s.replace(/<show_contact_form[^>]*\/?\s*>/gi, '')
  s = s.replace(/<show_date_picker[^>]*\/?\s*>/gi, '')
  s = s.replace(/<show_time_picker[^>]*\/?\s*>/gi, '')
  s = s.replace(/<\/show_contact_form>/gi, '')
  s = s.replace(/<\/show_date_picker>/gi, '')
  s = s.replace(/<\/show_time_picker>/gi, '')
  return s.trim()
}

/**
 * Strip all form/date/time encoding blocks from content so they never appear in the message body.
 * Forms and pickers are shown via interactive_suggestions only, not inline in the text.
 */
export function stripAllWidgetEncodingFromBody(content: string | null | undefined): string {
  if (!content || typeof content !== 'string') return ''
  let s = content
  // Strip function_calls markers
  s = s.replace(/<function_calls>[\s\S]*?<\/function_calls>/gi, '')
  s = s.replace(/<\/function_calls>/gi, '')
  // Strip widget XML markers (show_contact_form, show_date_picker, show_time_picker)
  s = s.replace(/<show_contact_form[^>]*\/?\s*>/gi, '')
  s = s.replace(/<show_date_picker[^>]*\/?\s*>/gi, '')
  s = s.replace(/<show_time_picker[^>]*\/?\s*>/gi, '')
  s = s.replace(/<\/show_contact_form>/gi, '')
  s = s.replace(/<\/show_date_picker>/gi, '')
  s = s.replace(/<\/show_time_picker>/gi, '')
  // Remove full blocks: [/date:...] ... [/date], [/time:...] ... [/time], [form]...[/form], [/form:...]...[/form]
  s = s.replace(/\[\/date(?::[^\]]*)?\][\s\S]*?\[\/date\]/gi, '')
  s = s.replace(/\[\/time(?::[^\]]*)?\][\s\S]*?\[\/time\]/gi, '')
  s = s.replace(/\[\/form(?::[^\]]*)?\][\s\S]*?\[\/form\]/gi, '')
  s = s.replace(/\[form\][\s\S]*?\[\/form\]/gi, '')
  s = s.replace(/\[options\][\s\S]*?\[\/options\]/gi, '')
  // Remove any remaining standalone encoding tags (opening or closing)
  s = s.replace(/\s*\[\/(?:form|date|time)(?::[^\]]*)?\]\s*/g, ' ')
  s = s.replace(/\s*\[\/(?:form|date|time)\]\s*/gi, ' ')
  return s.trimEnd().replace(/\n{3,}/g, '\n\n').trim()
}

function safeParseFormJson(line: string): FormSuggestion | null {
  try {
    const o = JSON.parse(line) as Record<string, unknown>
    const fieldsRaw = o.fields
    if (!Array.isArray(fieldsRaw) || fieldsRaw.length === 0) return null
    const title = typeof o.title === 'string' ? o.title : undefined
    const fields: FormFieldSpec[] = fieldsRaw.map((f: any) => {
      const id = (f?.id ?? 'field').toString().trim().toLowerCase().replace(/\s+/g, '_') || 'field'
      const label = (f?.label ?? id).toString().trim()
      const type = normalizeFieldType(f?.type)
      const placeholder = typeof f?.placeholder === 'string' ? f.placeholder : undefined
      const description = typeof f?.description === 'string' ? f.description : undefined
      const required = f?.required !== false
      const autocomplete = typeof f?.autocomplete === 'string' ? f.autocomplete : undefined
      return { id, label, type, placeholder, description, required, autocomplete }
    })
    return { type: 'form', title: title || 'Your details', fields }
  } catch {
    return null
  }
}

/** Parse [options]{...}[/options] JSON. options can be string[] or { id, label }[]. */
function safeParseOptionsJson(line: string): OptionsSuggestion | null {
  try {
    const o = JSON.parse(line) as Record<string, unknown>
    const raw = o.options
    if (!Array.isArray(raw) || raw.length === 0) return null
    const options: OptionsOption[] = raw.map((opt: any, i: number) => {
      if (typeof opt === 'string') {
        const id = `opt-${i}`
        return { id, label: opt }
      }
      const id = (opt?.id ?? `opt-${i}`).toString().trim()
      const label = (opt?.label ?? id).toString().trim()
      return { id, label }
    })
    const question = typeof o.question === 'string' ? o.question : undefined
    const multiple = o.multiple === true
    return { type: 'options', question, options, multiple }
  } catch {
    return null
  }
}

/** Generate time slots from range "HH:MM-HH:MM" in 30-minute steps. */
function slotsFromTimeRange(range: string): ScheduleSlot[] {
  const match = range.match(/^(\d{1,2}):(\d{2})-(\d{1,2}):(\d{2})$/)
  if (!match) return []
  const [, sh, sm, eh, em] = match.map(Number)
  let start = sh * 60 + sm
  const end = eh * 60 + em
  const slots: ScheduleSlot[] = []
  while (start < end) {
    const h = Math.floor(start / 60)
    const m = start % 60
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
    const ampm = h >= 12 ? 'PM' : 'AM'
    const label = `${h12}:${m.toString().padStart(2, '0')} ${ampm}`
    const id = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`
    slots.push({ id, label })
    start += 30
  }
  return slots
}

export function parseInteractiveEncoding(content: string | null | undefined): {
  cleanContent: string
  suggestions: InteractiveSuggestion[]
} {
  if (!content || typeof content !== 'string') return { cleanContent: '', suggestions: [] }
  const bracketForm = tryParseBracketForm(content)
  if (bracketForm) {
    return { cleanContent: bracketForm.cleanContent, suggestions: [bracketForm.suggestion] }
  }
  const structured = tryParseStructuredForm(content)
  if (structured) {
    return { cleanContent: structured.cleanContent, suggestions: [structured.suggestion] }
  }
  // Strip all [options]{...}[/options] and collect options suggestions
  let working = content
  const optionsSuggestions: OptionsSuggestion[] = []
  let optionsMatch = working.match(BRACKET_OPTIONS_REGEX)
  while (optionsMatch) {
    const parsed = safeParseOptionsJson(optionsMatch[1].trim())
    if (parsed) optionsSuggestions.push(parsed)
    working = working.replace(optionsMatch[0], '').trim()
    optionsMatch = working.match(BRACKET_OPTIONS_REGEX)
  }
  const match = working.match(INLINE_ENCODING_REGEX)
  if (!match) {
    return { cleanContent: working.trim(), suggestions: [...optionsSuggestions] }
  }
  const [, kind, payload] = match
  const cleanContent = working.slice(0, match.index).trim()
  const suggestions: InteractiveSuggestion[] = [...optionsSuggestions]
  if (kind === 'form' && payload) {
    const pipe = payload.indexOf('|')
    const title = pipe >= 0 ? payload.slice(0, pipe).trim() : undefined
    const fieldList = (pipe >= 0 ? payload.slice(pipe + 1) : payload).trim()
    const ids = fieldList.split(/\s*,\s*/).map(s => s.trim().toLowerCase().replace(/\s+/g, '_')).filter(Boolean)
    if (ids.length > 0) {
      const fields: FormFieldSpec[] = ids.map(id => ({
        id,
        label: id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        type: fieldIdToType(id),
        required: true
      }))
      suggestions.push({ type: 'form', title: title || 'Your details', fields } as FormSuggestion)
    }
  } else if (kind === 'date') {
    const title = (payload || '').trim() || 'Choose a date'
    suggestions.push({ type: 'schedule_calendar', title } as ScheduleCalendarSuggestion)
  } else if (kind === 'time') {
    const raw = (payload || '').trim()
    const rangeMatch = raw.match(/^\d{1,2}:\d{2}-\d{1,2}:\d{2}$/)
    if (rangeMatch) {
      const slots = slotsFromTimeRange(raw)
      suggestions.push({ type: 'schedule_time', title: 'Choose a time', slots } as ScheduleTimeSuggestion)
    } else {
      const title = raw || 'Choose a time'
      const slots = slotsFromTimeRange('09:00-17:00')
      suggestions.push({ type: 'schedule_time', title, slots } as ScheduleTimeSuggestion)
    }
  }
  return { cleanContent, suggestions }
}
